import { useEffect, useRef, useState } from "react";

// ── Константы — должны совпадать с FlappyPlayer ───────────────────────────────
const W      = 800;
const H      = 450;
const BIRD_R = 14;
const PIPE_W = 60;

export default function FlappyHost({ data, roundEnd, positions, alive, scores, tv }) {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);
  const frameRef  = useRef(0); // только для анимации крыльев

  const [phase, setPhase]  = useState("waiting");
  const [countdown, setCd] = useState(3);

  // positions — это ref из HostLobby, читаем .current напрямую в RAF (без ре-рендеров)
  const posRef  = positions; // уже ref
  const dataRef = useRef(null);
  useEffect(() => { dataRef.current = data || null; }, [data]);

  // ── Новый раунд ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!data || roundEnd) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    frameRef.current = 0;
    setPhase("countdown");
    setCd(3);
    let c = 3;
    const iv = setInterval(() => {
      c--;
      setCd(c);
      if (c <= 0) { clearInterval(iv); startRendering(); }
    }, 1000);
    return () => { clearInterval(iv); };
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  function startRendering() {
    if (!canvasRef.current) return;
    setPhase("playing");
    rafRef.current = requestAnimationFrame(renderLoop);
  }

  function renderLoop() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    frameRef.current++;
    // Рисуем каждый второй кадр (~30fps) — снижает нагрузку без потери качества
    if (frameRef.current % 2 === 0) {
      const ctx = canvas.getContext("2d");
      drawHost(ctx);
    }
    rafRef.current = requestAnimationFrame(renderLoop);
  }

  function drawHost(ctx) {
    const frame      = frameRef.current;
    const d          = dataRef.current;
    const gap        = d?.gap || 150;
    const birdColors = d?.players?.reduce((m, p) => { m[p.name] = p.color; return m; }, {}) || {};

    // Небо
    ctx.fillStyle = "#87CEEB";
    ctx.fillRect(0, 0, W, H);

    // Облака
    ctx.fillStyle = "#ffffffcc";
    [[120,55,60,25],[380,80,75,30],[620,45,55,22],[260,110,45,18]].forEach(([x,y,w,h]) => {
      ctx.beginPath(); ctx.ellipse(x, y, w, h, 0, 0, Math.PI*2); ctx.fill();
    });

    // ── Трубы из pipeData снепшота первого живого игрока ─────────────────────
    // Масштаб X: игрок W=400 → хост W=800; высота одинаковая H=450
    const XSCALE  = W / 400;
    const posVals = Object.values(posRef.current);

    const pipeData = (() => {
      for (const p of posVals) {
        if (p.alive !== false && p.pipeData && p.pipeData.length > 0) return p.pipeData;
      }
      for (const p of posVals) {
        if (p.pipeData && p.pipeData.length > 0) return p.pipeData;
      }
      return [];
    })();

    pipeData.forEach(p => {
      const px   = p.x * XSCALE;
      const gapY = p.gapY;
      const botY = gapY + gap;

      ctx.fillStyle = "#3AB54A";
      ctx.fillRect(px, 0, PIPE_W, gapY);
      ctx.fillStyle = "#2D8F3B";
      ctx.fillRect(px - 5, gapY - 22, PIPE_W + 10, 22);

      ctx.fillStyle = "#3AB54A";
      ctx.fillRect(px, botY, PIPE_W, H - botY);
      ctx.fillStyle = "#2D8F3B";
      ctx.fillRect(px - 5, botY, PIPE_W + 10, 22);
    });

    // Земля
    ctx.fillStyle = "#8B6914"; ctx.fillRect(0, H - 20, W, 20);
    ctx.fillStyle = "#5D9E2B"; ctx.fillRect(0, H - 24, W, 6);

    // ── Птички ────────────────────────────────────────────────────────────────
    const players    = d?.players || [];
    const posEntries = Object.entries(posRef.current);
    const renderList = posEntries.length > 0
      ? posEntries
      : players.map(p => [p.name, { x: 80, y: H / 2, vy: 0, pipes: 0, alive: true }]);

    renderList.forEach(([name, pos]) => {
      const color   = birdColors[name] || "#6BCB77";
      const isAlive = pos.alive !== false;
      const bx = pos.x * XSCALE;
      const by = pos.y; // H одинаковый у игрока и хоста

      ctx.save();
      ctx.globalAlpha = isAlive ? 1 : 0.3;
      ctx.translate(bx, by);
      const angle = isAlive ? Math.max(-0.5, Math.min(0.8, (pos.vy || 0) * 0.06)) : 0.8;
      ctx.rotate(angle);

      ctx.fillStyle = color;
      ctx.beginPath(); ctx.ellipse(0, 0, BIRD_R+2, BIRD_R, 0, 0, Math.PI*2); ctx.fill();
      ctx.strokeStyle = "#000"; ctx.lineWidth = 2; ctx.stroke();

      ctx.fillStyle = "#fff";
      ctx.beginPath(); ctx.arc(5, -4, 5, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = "#222";
      ctx.beginPath(); ctx.arc(6, -4, 2.5, 0, Math.PI*2); ctx.fill();

      ctx.fillStyle = "#FF8C00";
      ctx.beginPath(); ctx.moveTo(BIRD_R,-2); ctx.lineTo(BIRD_R+9,0); ctx.lineTo(BIRD_R,3); ctx.fill();

      const wingY = isAlive ? Math.sin(frame / 4) * 4 : 3;
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.ellipse(-2, wingY, 8, 5, -0.3, 0, Math.PI*2); ctx.fill();

      ctx.restore();

      ctx.font = `bold ${tv?"13px":"11px"} 'Nunito', sans-serif`;
      ctx.textAlign = "center";
      ctx.strokeStyle = "#000"; ctx.lineWidth = 3;
      ctx.strokeText(name, bx, by - BIRD_R - 6);
      ctx.fillStyle = "#fff";
      ctx.fillText(name, bx, by - BIRD_R - 6);

      if (isAlive) {
        ctx.font = `bold ${tv?"11px":"10px"} 'Nunito', sans-serif`;
        ctx.strokeStyle = "#000"; ctx.lineWidth = 2;
        ctx.strokeText(`×${pos.pipes||0}`, bx, by - BIRD_R - 18);
        ctx.fillStyle = "#ffe600";
        ctx.fillText(`×${pos.pipes||0}`, bx, by - BIRD_R - 18);
      }
    });
  }

  // ── Round end ─────────────────────────────────────────────────────────────────
  if (roundEnd) {
    const sorted = Object.entries(roundEnd.scores || {}).sort((a,b)=>b[1]-a[1]);
    return (
      <div style={{textAlign:"center",padding:"1.5rem"}}>
        <div style={{fontSize:"2.5rem",marginBottom:"0.5rem"}}>🐦</div>
        <h2 style={{fontFamily:"'Fredoka One',sans-serif",fontSize:tv?"1.8rem":"1.4rem",color:"#6BCB77",marginBottom:"1rem",textShadow:"2px 2px 0 #2d6b3a"}}>
          Раунд {roundEnd.round} завершён!
        </h2>
        {roundEnd.survivor&&(
          <p style={{fontFamily:"'Nunito',sans-serif",fontSize:tv?"1.1rem":"0.85rem",color:"#ffe600",marginBottom:"0.75rem"}}>
            🏆 Последний выживший: <strong>{roundEnd.survivor}</strong> +200 бонус
          </p>
        )}
        <div style={{display:"flex",flexWrap:"wrap",gap:"0.5rem",justifyContent:"center",marginBottom:"0.75rem"}}>
          {sorted.map(([name,pipes],i)=>(
            <div key={name} style={{background:i===0?"#ffe60022":"#110825",border:`2px solid ${i===0?"#ffe600":"#ffffff11"}`,borderRadius:"0.75rem",padding:"0.4rem 0.85rem",fontSize:tv?"1rem":"0.85rem",color:i===0?"#ffe600":"#aaa"}}>
              {["🥇","🥈","🥉"][i]||"  "} {name} — {pipes} труб{name===roundEnd.survivor?" 🏆":""}
            </div>
          ))}
        </div>
        <p style={{fontFamily:"'Nunito',sans-serif",color:"#444",fontSize:"0.85rem"}}>Следующий раунд...</p>
      </div>
    );
  }

  if (!data) return null;

  // Scoreboard
  const playerList = data.players || [];

  return (
    <div style={{width:"100%",textAlign:"center"}}>
      {phase === "countdown" && (
        <div style={{position:"relative"}}>
          <canvas ref={canvasRef} width={W} height={H} style={{width:"100%",maxWidth:W,borderRadius:"1rem",border:"3px solid #6BCB77",display:"block",margin:"0 auto",opacity:0.4}} />
          <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontFamily:"'Fredoka One',sans-serif",fontSize:tv?"8rem":"6rem",color:"#ffe600",textShadow:"4px 4px 0 #000",pointerEvents:"none"}}>
            {countdown||"GO!"}
          </div>
        </div>
      )}
      {phase === "playing" && (
        <div style={{position:"relative"}}>
          <canvas ref={canvasRef} width={W} height={H} style={{width:"100%",maxWidth:W,borderRadius:"1rem",border:"3px solid #6BCB77",display:"block",margin:"0 auto"}} />
          {/* Scoreboard */}
          <div style={{position:"absolute",top:"0.5rem",right:"0.5rem",background:"#000000aa",borderRadius:"0.75rem",padding:"0.4rem 0.6rem"}}>
            {playerList.map(p => {
              const isAlive = alive instanceof Set ? alive.has(p.name) : (alive?.[p.name] !== false);
              const sc = scores?.[p.name] ?? 0;
              return (
                <div key={p.name} style={{fontFamily:"'Nunito',sans-serif",fontSize:tv?"0.9rem":"0.75rem",color:isAlive?(p.color||"#fff"):"#555",display:"flex",gap:"0.3rem",alignItems:"center"}}>
                  <span>{isAlive?"🐦":"💀"}</span>
                  <span>{p.name}</span>
                  <span style={{color:"#ffe600",marginLeft:"0.2rem"}}>×{sc}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {phase === "waiting" && (
        <div style={{fontFamily:"'Fredoka One',sans-serif",color:"#6BCB77",padding:"3rem",fontSize:"1.2rem"}}>Загрузка игры...</div>
      )}
    </div>
  );
}